import { findAnchor } from './tour-anchor';
import { TourAction, TourStep } from './tour.model';

/**
 * Le temps laissé à l'élément d'un geste pour apparaître et s'activer, quand le geste précédent de
 * l'étape a pu ouvrir une fenêtre ou lancer une saisie différée.
 */
const GESTURE_WAIT_MS = 2000;

/** L'élément sur lequel porte l'action : l'ancre elle-même, ou un élément qu'elle contient. */
export function actionTarget(anchor: HTMLElement, action: TourAction): HTMLElement | null {
  return action.selector ? anchor.querySelector<HTMLElement>(action.selector) : anchor;
}

/** Un élément qu'on ne peut pas actionner : désactivé lui-même ou par un parent. */
export function isDisabled(element: HTMLElement): boolean {
  return !!element.closest('[disabled], [aria-disabled="true"]');
}

/** L'élément d'un geste de l'étape, à l'écran et actionnable ; `null` sinon. */
export function gestureTarget(
  step: TourStep,
  action: TourAction,
  root: ParentNode = document,
): HTMLElement | null {
  const anchor = findAnchor(action.anchor ?? step.anchor, root);
  const target = anchor && actionTarget(anchor, action);
  return target && !isDisabled(target) ? target : null;
}

/** Un geste en mots, pour le panneau et le rejeu : sa nature et sa cible, jamais sa valeur. */
export function describeGesture(action: TourAction): string {
  const kind = action.kind === 'click' ? 'un clic' : 'une saisie';
  const target = [action.anchor, action.selector].filter(Boolean).join(' ');
  return target ? `${kind} (${target})` : kind;
}

function act(target: HTMLElement, action: TourAction): void {
  if (action.kind === 'click') {
    target.click();
    return;
  }
  const input = target as HTMLInputElement;
  input.focus();
  input.value = typeof action.value === 'function' ? action.value() : action.value;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
  input.dispatchEvent(new Event('blur'));
}

async function waitForTarget(
  step: TourStep,
  action: TourAction,
  root: ParentNode,
  waitMs: number,
): Promise<HTMLElement | null> {
  const deadline = Date.now() + waitMs;
  let target = gestureTarget(step, action, root);
  while (!target && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 50));
    target = gestureTarget(step, action, root);
  }
  return target;
}

/**
 * Fait les gestes de l'étape, dans l'ordre, comme l'utilisateur les aurait faits. Le premier vise
 * ce qui est à l'écran ; chacun des suivants attend son élément, que le précédent a pu faire
 * apparaître. Rend l'indice du premier geste qui n'a pas pu se faire — les suivants ne se font
 * pas —, ou `null` quand tous se sont faits.
 */
export async function performStep(
  step: TourStep,
  { root = document as ParentNode, waitMs = GESTURE_WAIT_MS } = {},
): Promise<number | null> {
  for (const [index, action] of (step.actions ?? []).entries()) {
    const target =
      index === 0
        ? gestureTarget(step, action, root)
        : await waitForTarget(step, action, root, waitMs);
    if (!target) return index;
    act(target, action);
  }
  return null;
}
